"use client";

import { Capability } from "./prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import CapabilityPill from "./capability-pill";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { InferenceStatus, SegmentResult } from "@/lib/playground-utils";
import { sanitizeSegmentPath, type SanitizedPathResult } from "./segmentation-path";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { Maximize2, X } from "lucide-react";
import CodeExample from "./code-example";
import { Spinner } from "@/components/ui/spinner";

const SEGMENT_RENDER_DEBUG_PREFIX = "[SegRender]";
const logSegRender = (...args: unknown[]) => {
  console.log(SEGMENT_RENDER_DEBUG_PREFIX, ...args);
};

const HOVER_SCALE_FACTOR = 1.07;

const isSegmentResultShape = (value: unknown): value is SegmentResult => {
  if (!value || typeof value !== "object") return false;
  const maybe = value as Record<string, unknown>;
  if (typeof maybe.path !== "string") return false;
  const bbox = maybe.bbox as Record<string, unknown> | undefined;
  if (!bbox) return false;
  return ["x_min", "y_min", "x_max", "y_max"].every(
    (key) => typeof bbox[key] === "number",
  );
};

type InferenceSegmentationProps = {
  image: string;
  result: string;
  query: string;
  duration?: number;
  status?: InferenceStatus;
  segmentResult?: SegmentResult;
  segmentResults?: Array<SegmentResult | null>;
  neonHue?: number;
  displayCapability?: Capability;
  imageFile?: File;
};

const buildNeonPalette = (hue: number) => {
  // Neon colors pop with full saturation and mid-to-high lightness
  return {
    glow: `hsla(${hue}, 100%, 60%, 0.7)`,
    core: `hsla(${hue}, 100%, 75%, 0.9)`,
    coreHighlight: `hsla(${hue}, 100%, 90%, 1)`,
    fill: `hsl(${hue}, 100%, 65%)`,
  };
};

export default function InferenceSegmentation({
  image,
  result,
  query,
  duration,
  status,
  segmentResult,
  segmentResults,
  neonHue,
  displayCapability,
  imageFile,
}: InferenceSegmentationProps) {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const lastValidPathByIdxRef = useRef<Map<number, SanitizedPathResult>>(new Map());
  const [pulseKey, setPulseKey] = useState(0);
  const defsIdBaseRef = useRef(`seg-${Math.random().toString(36).slice(2)}`);
  const fallbackHueRef = useRef<number>(Math.floor(Math.random() * 360));
  const hueMapRef = useRef<Map<number, number>>(new Map());
  const resolvedHue = neonHue ?? fallbackHueRef.current;
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const activeSegment = useMemo<SegmentResult | undefined>(() => {
    const first = segmentResults?.find((s) => s?.path?.length);
    if (first) return first;
    if (segmentResult && segmentResult.path?.length) {
      return segmentResult;
    }
    try {
      const parsed = JSON.parse(result);
      if (isSegmentResultShape(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore invalid JSON until stream catches up */
    }
    return undefined;
  }, [segmentResult, segmentResults, result]);

  useEffect(() => {
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      if (cancelled) return;
      const viewportLimit =
        typeof window !== "undefined" ? Math.min(window.innerWidth - 64, 1800) : 1200;
      const MAX_DIMENSION = isExpanded ? Math.max(512, viewportLimit) : 512;
      let scaledWidth = img.width;
      let scaledHeight = img.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      setDimensions({ width: scaledWidth, height: scaledHeight });
      logSegRender("imageLoaded", { scaledWidth, scaledHeight });
    };

    img.src = image;
    logSegRender("imageLoading", { srcLength: image.length });

    return () => {
      cancelled = true;
    };
  }, [image, isExpanded]);

  useEffect(() => {
    logSegRender("segmentResult update", {
      hasSegmentResult: !!segmentResult,
      pathLen: segmentResult?.path.length ?? 0,
    });
  }, [segmentResult]);

  useEffect(() => {
    logSegRender("activeSegment", {
      hasActive: !!activeSegment,
      pathLen: activeSegment?.path.length ?? 0,
    });
  }, [activeSegment]);

  useEffect(() => {
    logSegRender("result string", { length: result.length });
  }, [result]);

  const overlayList = useMemo<
    Array<{ path: string; x: number; y: number; scaleX: number; scaleY: number; indexForHue: number }>
  >(() => {
    const candidatesWithIndex: Array<{ seg: SegmentResult | null; idx: number }> =
      segmentResults && segmentResults.length
        ? segmentResults.map((seg, idx) => ({ seg: seg ?? null, idx })).filter(({ seg }) => Boolean(seg?.path?.length))
        : activeSegment
          ? [{ seg: activeSegment, idx: 0 }]
          : [];

    return candidatesWithIndex
      .map(({ seg: segment, idx }) => {
        if (!segment) return null;
        const sanitized = sanitizeSegmentPath(segment.path);
        const candidate = sanitized ?? lastValidPathByIdxRef.current.get(idx) ?? null;
        if (!candidate?.path) return null;
        if (sanitized) {
          lastValidPathByIdxRef.current.set(idx, sanitized);
          if (sanitized.truncated) {
            logSegRender("path truncated for rendering", {
              originalLen: segment.path.length,
              truncatedLen: sanitized.path.length,
            });
          }
        }

        const bbox = segment.bbox;
        const { width, height } = dimensions;
        if (width === 0 || height === 0) return null;

        const bboxWidthPx = (bbox.x_max - bbox.x_min) * width;
        const bboxHeightPx = (bbox.y_max - bbox.y_min) * height;
        const bboxX = bbox.x_min * width;
        const bboxY = bbox.y_min * height;

        if (bboxWidthPx <= 0 || bboxHeightPx <= 0) return null;

        return {
          path: candidate.path,
          x: bboxX,
          y: bboxY,
          scaleX: bboxWidthPx,
          scaleY: bboxHeightPx,
          indexForHue: idx,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b!.scaleX * b!.scaleY) - (a!.scaleX * a!.scaleY)) as Array<{
        path: string;
        x: number;
        y: number;
        scaleX: number;
        scaleY: number;
        indexForHue: number;
      }>;
  }, [activeSegment, dimensions, segmentResults]);

  const showSpinner = overlayList.length === 0;

  const overlaySignature = overlayList
    .map((o) => `${o.indexForHue}:${o.path.length}:${o.x.toFixed(2)}:${o.y.toFixed(2)}:${o.scaleX.toFixed(2)}:${o.scaleY.toFixed(2)}`)
    .join("|") || "none";

  const plannedSegments = segmentResults?.length ?? overlayList.length;
  const labelText = `${plannedSegments} found`;

  // Fire a short glow pulse the first time we hit "done" with a renderable overlay.
  const prevStatusRef = useRef<InferenceStatus | undefined>(status);
  useEffect(() => {
    if (status === "done" && prevStatusRef.current !== "done" && overlayList.length > 0) {
      setPulseKey((k) => k + 1);
    }
    prevStatusRef.current = status;
  }, [status, overlayList.length, overlaySignature]);

  useEffect(() => {
    if (!activeSegment) {
      logSegRender("overlay-blocked", "no active segment yet");
      return;
    }
    if (dimensions.width === 0 || dimensions.height === 0) {
      logSegRender("overlay-blocked", {
        reason: "dimensions",
        dimensions,
      });
      return;
    }
    if (!overlayList.length) {
      const bbox = activeSegment.bbox;
      logSegRender("overlay-blocked", {
        reason: "bbox-size",
        bbox,
      });
      return;
    }
    logSegRender("overlay-ready", {
      signature: overlaySignature,
      pathPreview: overlayList[0]?.path.slice(0, 32),
    });
  }, [overlaySignature, overlayList, activeSegment, dimensions]);

  return (
    <>
      <Card>
        <CardContent className="mt-6">
          <div>
            <div className="flex flex-col gap-4 sm:gap-6">
              <div className="flex flex-col gap-2 min-w-0 flex-grow">
                <div className="flex flex-row gap-2 items-center">
                  <CapabilityPill capability={displayCapability ?? Capability.Segmentation} />
                  <div className="text-sm break-words">{query}</div>
                </div>
              </div>
              <div className="w-full max-w-[600px]">
                <div
                  className="relative rounded-lg overflow-hidden bg-black/5"
                  style={{
                    width: "100%",
                    aspectRatio:
                      dimensions.width && dimensions.height
                        ? `${dimensions.width} / ${dimensions.height}`
                        : "1 / 1",
                  }}
                >
                  <svg
                    className="absolute inset-0 w-full h-full"
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <image
                      href={image}
                      width="100%"
                      height="100%"
                      preserveAspectRatio="xMidYMid meet"
                    />
                    <rect
                      width="100%"
                      height="100%"
                      fill="black"
                      opacity={status === "done" ? 0.65 : 0}
                      style={{ transition: "opacity 150ms ease" }}
                    />
                  </svg>

                  <AnimatePresence>
                    {showSpinner && (
                      <motion.div
                        key="seg-spinner"
                        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <div className="rounded-md bg-background/80 px-3 py-2 shadow-sm ring-1 ring-border">
                          <Spinner size="small" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {overlayList.length > 0 &&
                    overlayList.map((overlay) => {
                      const idx = overlay.indexForHue;
                      if (!hueMapRef.current.has(idx)) {
                        const assignedHue = (resolvedHue + idx * 137) % 360;
                        hueMapRef.current.set(idx, assignedHue);
                      }
                      const hue = hueMapRef.current.get(idx)!;
                      const palette = buildNeonPalette(hue);
                      const gradientId = `${defsIdBaseRef.current}-grad-${idx}`;
                      const clipPathId = `${defsIdBaseRef.current}-clip-${idx}`;
                      const glowId = `${defsIdBaseRef.current}-glow-${idx}`;
                      const transform = `translate(${overlay.x} ${overlay.y}) scale(${overlay.scaleX} ${overlay.scaleY})`;
                      const hoverScale = status === "done" ? HOVER_SCALE_FACTOR : 1;
                      const segmentCenterX = overlay.x + overlay.scaleX / 2;
                      const segmentCenterY = overlay.y + overlay.scaleY / 2;
                      const originXPercent =
                        dimensions.width === 0 ? 50 : (segmentCenterX / dimensions.width) * 100;
                      const originYPercent =
                        dimensions.height === 0 ? 50 : (segmentCenterY / dimensions.height) * 100;

                      const isHoverable = status === "done";
                      const isHovered = hoveredIdx === idx;

                      return (
                        <motion.svg
                          key={`${overlaySignature}-${idx}`}
                          className="absolute inset-0 w-full h-full"
                          width="100%"
                          height="100%"
                          viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
                          preserveAspectRatio="xMidYMid meet"
                          style={{
                            pointerEvents: "none",
                          }}
                        >
                          <defs>
                            <clipPath id={clipPathId} clipPathUnits="userSpaceOnUse">
                              <path d={overlay.path} transform={transform} />
                            </clipPath>

                            <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceGraphic" stdDeviation="0.02" result="blur" />
                              <feColorMatrix
                                in="blur"
                                type="matrix"
                                values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 0.8 0"
                              />
                            </filter>

                            <linearGradient
                              id={gradientId}
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="0%"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop offset="0%">
                                <animate
                                  attributeName="stop-color"
                                  values={`${palette.core};${palette.coreHighlight};${palette.core}`}
                                  dur="2s"
                                  repeatCount="indefinite"
                                />
                              </stop>
                              <stop offset="100%">
                                <animate
                                  attributeName="stop-color"
                                  values={`${palette.coreHighlight};${palette.core};${palette.coreHighlight}`}
                                  dur="2s"
                                  repeatCount="indefinite"
                                />
                              </stop>
                              <animateTransform
                                attributeName="gradientTransform"
                                type="translate"
                                values="0 0; 1 0; 0 0"
                                dur="2s"
                                repeatCount="indefinite"
                              />
                            </linearGradient>
                          </defs>

                          <motion.g
                            style={{
                              pointerEvents: "auto",
                              transformOrigin: `${originXPercent}% ${originYPercent}%`,
                              transformBox: "fill-box",
                              cursor: isHoverable ? "pointer" : "default",
                            }}
                            onHoverStart={() => {
                              if (isHoverable) setHoveredIdx(idx);
                            }}
                            onHoverEnd={() => {
                              setHoveredIdx((current) => (current === idx ? null : current));
                            }}
                            animate={isHoverable && isHovered ? { scale: hoverScale } : { scale: 1 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <g clipPath={`url(#${clipPathId})`}>
                              {/* Full-brightness image clipped to the segment */}
                              <image
                                href={image}
                                width="100%"
                                height="100%"
                                preserveAspectRatio="xMidYMid meet"
                              />
                            </g>

                            <g transform={transform}>
                              {status === "done" ? (
                                <motion.path
                                  key={`fill-${pulseKey}-${idx}`}
                                  d={overlay.path.endsWith("Z") ? overlay.path : `${overlay.path} Z`}
                                  fill={palette.fill}
                                  stroke="none"
                                  vectorEffect="non-scaling-stroke"
                                  initial={{ opacity: 0 }}
                                  animate={{
                                    opacity: [0, 0.2, 0],
                                    transition: {
                                      duration: 0.7,
                                      times: [0, 0.35, 1],
                                      ease: "easeOut",
                                    },
                                  }}
                                />
                              ) : null}

                              <motion.path
                                key={`glow-${pulseKey}-${idx}`}
                                d={overlay.path}
                                vectorEffect="non-scaling-stroke"
                                stroke={palette.glow}
                                strokeWidth={20}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                                filter={`url(#${glowId})`}
                                initial={{ opacity: 0.7, strokeWidth: 20 }}
                                animate={
                                  status === "done"
                                    ? {
                                        opacity: [0.7, 1, 0],
                                        strokeWidth: [20, 26, 0],
                                        transition: {
                                          duration: 0.5,
                                          times: [0, 0.2, 1],
                                          ease: "easeOut",
                                        },
                                      }
                                    : { opacity: 0.7, strokeWidth: 20 }
                                }
                              />
                              <path
                                className="segmentation-core"
                                d={overlay.path}
                                vectorEffect="non-scaling-stroke"
                                stroke={`url(#${gradientId})`}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                          </motion.g>
                        </motion.svg>
                      );
                    })}

                <div
                  className="absolute left-2 top-2 z-10 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium text-white shadow-sm backdrop-blur-sm pointer-events-none"
                  aria-live="polite"
                >
                  {labelText}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 z-10 h-7 w-7 rounded-md bg-black/60 text-white hover:bg-black/70"
                  onClick={() => setIsExpanded(true)}
                >
                  <Maximize2 className="h-4 w-4" aria-hidden />
                  <span className="sr-only">Expand image</span>
                </Button>
              </div>
              {status === "done" && (
                <div className="flex flex-col gap-2 mt-2 w-full">
                  {imageFile && (
                    <CodeExample
                      capability={displayCapability ?? Capability.Segmentation}
                      prompt={query}
                      imageFile={imageFile}
                    />
                  )}
                  {duration && (
                    <div className="text-xs text-muted-foreground">
                      Response time: {duration.toFixed(0)}ms
                    </div>
                  )}
                </div>
              )}
              </div>

            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent
          className="p-0 border-none bg-transparent shadow-none w-[90vw] max-w-[90vw] sm:max-w-[90vw] md:max-w-[90vw] lg:max-w-[90vw] xl:max-w-[90vw] max-h-[90vh] overflow-hidden"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Expanded segmentation result</DialogTitle>
          </DialogHeader>
          <div className="relative w-full">
            <div
              className="relative overflow-hidden rounded-lg bg-black/5 w-full"
              style={{
                width: "100%",
                maxWidth: "100%",
                aspectRatio:
                  dimensions.width && dimensions.height
                    ? `${dimensions.width} / ${dimensions.height}`
                    : "1 / 1",
              }}
            >
              <svg
                className="absolute inset-0 w-full h-full"
                width="100%"
                height="100%"
                viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <image
                  href={image}
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMidYMid meet"
                />
                <rect
                  width="100%"
                  height="100%"
                  fill="black"
                  opacity={status === "done" ? 0.65 : 0}
                  style={{ transition: "opacity 150ms ease" }}
                />
              </svg>

              <AnimatePresence>
                {showSpinner && (
                  <motion.div
                    key="seg-spinner-modal"
                    className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <div className="rounded-md bg-background/80 px-3 py-2 shadow-sm ring-1 ring-border">
                      <Spinner size="small" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {overlayList.length > 0 &&
                overlayList.map((overlay) => {
                  const idx = overlay.indexForHue;
                  if (!hueMapRef.current.has(idx)) {
                    const assignedHue = (resolvedHue + idx * 137) % 360;
                    hueMapRef.current.set(idx, assignedHue);
                  }
                  const hue = hueMapRef.current.get(idx)!;
                  const palette = buildNeonPalette(hue);
                  const gradientId = `${defsIdBaseRef.current}-modal-grad-${idx}`;
                  const clipPathId = `${defsIdBaseRef.current}-modal-clip-${idx}`;
                  const glowId = `${defsIdBaseRef.current}-modal-glow-${idx}`;
                  const transform = `translate(${overlay.x} ${overlay.y}) scale(${overlay.scaleX} ${overlay.scaleY})`;
                  const hoverScale = status === "done" ? HOVER_SCALE_FACTOR : 1;
                  const segmentCenterX = overlay.x + overlay.scaleX / 2;
                  const segmentCenterY = overlay.y + overlay.scaleY / 2;
                  const originXPercent =
                    dimensions.width === 0 ? 50 : (segmentCenterX / dimensions.width) * 100;
                  const originYPercent =
                    dimensions.height === 0 ? 50 : (segmentCenterY / dimensions.height) * 100;

                  const isHoverable = status === "done";
                  const isHovered = hoveredIdx === idx;

                  return (
                    <motion.svg
                      key={`${overlaySignature}-modal-${idx}`}
                      className="absolute inset-0 w-full h-full"
                      width="100%"
                      height="100%"
                      viewBox={`0 0 ${dimensions.width || 1} ${dimensions.height || 1}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{
                        pointerEvents: "none",
                      }}
                    >
                      <defs>
                        <clipPath id={clipPathId} clipPathUnits="userSpaceOnUse">
                          <path d={overlay.path} transform={transform} />
                        </clipPath>

                        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur in="SourceGraphic" stdDeviation="0.02" result="blur" />
                          <feColorMatrix
                            in="blur"
                            type="matrix"
                            values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 0.8 0"
                          />
                        </filter>

                        <linearGradient
                          id={gradientId}
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%">
                            <animate
                              attributeName="stop-color"
                              values={`${palette.core};${palette.coreHighlight};${palette.core}`}
                              dur="2s"
                              repeatCount="indefinite"
                            />
                          </stop>
                          <stop offset="100%">
                            <animate
                              attributeName="stop-color"
                              values={`${palette.coreHighlight};${palette.core};${palette.coreHighlight}`}
                              dur="2s"
                              repeatCount="indefinite"
                            />
                          </stop>
                          <animateTransform
                            attributeName="gradientTransform"
                            type="translate"
                            values="0 0; 1 0; 0 0"
                            dur="2s"
                            repeatCount="indefinite"
                          />
                        </linearGradient>
                      </defs>

                      <motion.g
                        style={{
                          pointerEvents: "auto",
                          transformOrigin: `${originXPercent}% ${originYPercent}%`,
                          transformBox: "fill-box",
                          cursor: isHoverable ? "pointer" : "default",
                        }}
                        onHoverStart={() => {
                          if (isHoverable) setHoveredIdx(idx);
                        }}
                        onHoverEnd={() => {
                          setHoveredIdx((current) => (current === idx ? null : current));
                        }}
                        animate={isHoverable && isHovered ? { scale: hoverScale } : { scale: 1 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <g clipPath={`url(#${clipPathId})`}>
                          {/* Full-brightness image clipped to the segment */}
                          <image
                            href={image}
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid meet"
                          />
                        </g>

                        <g transform={transform}>
                          {status === "done" ? (
                            <motion.path
                              key={`fill-${pulseKey}-modal-${idx}`}
                              d={overlay.path.endsWith("Z") ? overlay.path : `${overlay.path} Z`}
                              fill={palette.fill}
                              stroke="none"
                              vectorEffect="non-scaling-stroke"
                              initial={{ opacity: 0 }}
                              animate={{
                                opacity: [0, 0.2, 0],
                                transition: {
                                  duration: 0.7,
                                  times: [0, 0.35, 1],
                                  ease: "easeOut",
                                },
                              }}
                            />
                          ) : null}

                          <motion.path
                            key={`glow-${pulseKey}-modal-${idx}`}
                            d={overlay.path}
                            vectorEffect="non-scaling-stroke"
                            stroke={palette.glow}
                            strokeWidth={20}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                            filter={`url(#${glowId})`}
                            initial={{ opacity: 0.7, strokeWidth: 20 }}
                            animate={
                              status === "done"
                                ? {
                                    opacity: [0.7, 1, 0],
                                    strokeWidth: [20, 26, 0],
                                    transition: {
                                      duration: 0.5,
                                      times: [0, 0.2, 1],
                                      ease: "easeOut",
                                    },
                                  }
                                : { opacity: 0.7, strokeWidth: 20 }
                            }
                          />
                          <path
                            className="segmentation-core"
                            d={overlay.path}
                            vectorEffect="non-scaling-stroke"
                            stroke={`url(#${gradientId})`}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                      </motion.g>
                    </motion.svg>
                  );
                })}

              <div
                className="absolute left-3 top-3 z-10 rounded-md bg-black/60 px-3 py-1 text-[11px] font-medium text-white shadow-sm backdrop-blur-sm pointer-events-none"
                aria-live="polite"
              >
                {labelText}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-3 top-3 z-10 h-8 w-8 rounded-md bg-black/60 text-white hover:bg-black/70"
                onClick={() => setIsExpanded(false)}
              >
                <X className="h-4 w-4" aria-hidden />
                <span className="sr-only">Close expanded image</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
